// ============================================================
// RouteMiniMap — Composant carte Google Maps réutilisable
// ------------------------------------------------------------
// Affiche :
//   - Les polylines du trajet
//   - Les markers numérotés des arrêts (cueillette = orange, livraison = vert)
//   - Auto-fit sur l'étendue de la route
// Mode compact (preview sur OptionCard) ou grand (modal plein écran)
// ============================================================
import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

let googleMapsLoadingPromise = null;

const loadGoogleMaps = () => {
  if (window.google?.maps) return Promise.resolve();
  if (googleMapsLoadingPromise) return googleMapsLoadingPromise;
  googleMapsLoadingPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existing) {
      existing.addEventListener('load', resolve);
      if (window.google?.maps) resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}`;
    s.async = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return googleMapsLoadingPromise;
};

/**
 * props:
 *  - polylineCoords: [[lat, lng], ...]   polyline du trajet (optionnel)
 *  - stops: [{id, lat, lng, label, stop_type}]   points numérotés
 *  - compact: boolean   mode preview petit (sans contrôles)
 *  - onExpand: () => void   si fourni, un clic sur la carte compact l'appelle
 *  - height: string css   hauteur (default: 100%)
 */
const RouteMiniMap = ({ polylineCoords = [], stops = [], compact = false, onExpand, height = '100%' }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const polylineRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    loadGoogleMaps()
      .then(() => {
        if (!mounted || !containerRef.current || mapRef.current) return;
        try {
          mapRef.current = new window.google.maps.Map(containerRef.current, {
            center: { lat: 46.5, lng: -72.5 }, // Québec centre
            zoom: 7,
            disableDefaultUI: compact,
            zoomControl: !compact,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: !compact,
            gestureHandling: compact ? 'none' : 'auto',
            clickableIcons: false,
            styles: [
              { elementType: 'geometry', stylers: [{ color: '#1a1a2e' }] },
              { elementType: 'labels.text.stroke', stylers: [{ color: '#1a1a2e' }] },
              { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
              { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38414e' }] },
              { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#746855' }] },
              { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
              { featureType: 'poi', stylers: [{ visibility: 'off' }] },
            ],
          });
          setReady(true);
        } catch (e) {
          console.error('Mini-map init error:', e);
          setError(e.message);
        }
      })
      .catch((e) => {
        console.error('Google Maps load error:', e);
        setError('Impossible de charger Google Maps');
      });
    return () => { mounted = false; };
  }, [compact]);

  // Dessiner les markers + polyline à chaque changement
  useEffect(() => {
    if (!ready || !mapRef.current || !window.google?.maps) return;
    const map = mapRef.current;
    const g = window.google.maps;

    // Nettoyer anciens markers + polyline
    markersRef.current.forEach((m) => m.setMap?.(null));
    markersRef.current = [];
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }

    const bounds = new g.LatLngBounds();
    let hasBounds = false;

    // Polyline du trajet
    if (polylineCoords && polylineCoords.length > 1) {
      const path = polylineCoords
        .filter((c) => Array.isArray(c) && c.length >= 2 && Number.isFinite(c[0]) && Number.isFinite(c[1]))
        .map(([lat, lng]) => ({ lat, lng }));
      if (path.length > 1) {
        polylineRef.current = new g.Polyline({
          path,
          geodesic: true,
          strokeColor: '#f59e0b',
          strokeOpacity: 0.9,
          strokeWeight: compact ? 2 : 4,
          map,
        });
        path.forEach((p) => { bounds.extend(p); hasBounds = true; });
      }
    }

    // Markers numérotés
    (stops || []).forEach((s, idx) => {
      if (!Number.isFinite(s.lat) || !Number.isFinite(s.lng)) return;
      const stopType = (s.stop_type || '').toLowerCase();
      const isPickup = stopType === 'cueillette' || stopType === 'pickup';
      const color = isPickup ? '#f97316' : '#10b981';
      const marker = new g.Marker({
        position: { lat: s.lat, lng: s.lng },
        map,
        title: s.label || `Arrêt ${idx + 1}`,
        label: compact ? null : { text: String(idx + 1), color: '#fff', fontWeight: 'bold', fontSize: '11px' },
        icon: {
          path: g.SymbolPath.CIRCLE,
          scale: compact ? 4 : 10,
          fillColor: color,
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: compact ? 1 : 2,
        },
      });
      markersRef.current.push(marker);
      bounds.extend({ lat: s.lat, lng: s.lng });
      hasBounds = true;
    });

    // Auto-fit
    if (hasBounds) {
      try {
        map.fitBounds(bounds, compact ? 10 : 50);
      } catch (_) { /* no-op */ }
    }
  }, [ready, polylineCoords, stops, compact]);

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="w-full h-full flex items-center justify-center text-xs text-slate-500 bg-slate-900 rounded" style={{ height }}>
        Google Maps non configuré
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center text-xs text-red-400 bg-slate-900 rounded p-2 text-center" style={{ height }}>
        {error}
      </div>
    );
  }

  const content = (
    <>
      <div ref={containerRef} className="w-full h-full rounded" style={{ height }} data-testid="route-mini-map" />
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80 rounded">
          <Loader2 className="animate-spin text-amber-400" size={compact ? 16 : 28} />
        </div>
      )}
    </>
  );

  if (compact && onExpand) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); onExpand(); }}
        className="relative w-full block overflow-hidden rounded border border-slate-700 hover:border-amber-400 transition-colors"
        style={{ height }}
        data-testid="route-mini-map-expand-btn"
        title="Cliquer pour agrandir la carte"
      >
        {content}
      </button>
    );
  }

  return (
    <div className="relative w-full overflow-hidden rounded" style={{ height }}>
      {content}
    </div>
  );
};

export default RouteMiniMap;
